import { createReducer, on } from '@ngrx/store';
import { initialFeedState, FeedTabData } from './feed.state';
import * as FeedActions from './feed.actions';
import { CommunityTabType } from '../../interfaces';

export const feedReducer = createReducer(
  initialFeedState,

  on(FeedActions.loadTabItems, (state, { tabId }) => ({
    ...state,
    tabs: {
      ...state.tabs,
      [tabId]: {
        ...(state.tabs[tabId as CommunityTabType] || {
          items: [],
          currentPage: 0,
          hasMore: false,
          scrollPosition: 0,
        }),
        isLoading: true,
      },
    },
  })),

  on(FeedActions.loadTabItemsSuccess, (state, { tabId, items, currentPage, hasMore, isFirstLoad }) => {
    const existingTabData = state.tabs[tabId as CommunityTabType];
    const newTabData: FeedTabData = {
      items: isFirstLoad ? items : [...(existingTabData?.items || []), ...items],
      currentPage,
      hasMore,
      isLoading: false,
      scrollPosition: existingTabData?.scrollPosition || 0,
      lastViewedPostId: existingTabData?.lastViewedPostId,
      targetPostId: existingTabData?.targetPostId,
    };

    return {
      ...state,
      tabs: {
        ...state.tabs,
        [tabId]: newTabData,
      },
    };
  }),

  on(FeedActions.loadTabItemsFailure, (state, { tabId }) => ({
    ...state,
    tabs: {
      ...state.tabs,
      [tabId]: {
        ...(state.tabs[tabId as CommunityTabType] || {
          items: [],
          currentPage: 0,
          hasMore: false,
          scrollPosition: 0,
        }),
        isLoading: false,
      },
    },
  })),

  on(FeedActions.setTabScrollPosition, (state, { tabId, position, documentHeight }) => ({
    ...state,
    tabs: {
      ...state.tabs,
      [tabId]: {
        ...(state.tabs[tabId as CommunityTabType] || {
          items: [],
          currentPage: 0,
          hasMore: false,
          isLoading: false,
        }),
        scrollPosition: position,
        documentHeight: documentHeight,
        lastViewedPostId: state.tabs[tabId as CommunityTabType]?.lastViewedPostId,
        targetPostId: state.tabs[tabId as CommunityTabType]?.targetPostId,
      },
    },
  })),

  on(FeedActions.clearTabData, (state, { tabId }) => {
    const { [tabId]: removedTab, ...remainingTabs } = state.tabs;
    return {
      ...state,
      tabs: remainingTabs,
    };
  }),

  on(FeedActions.clearAllTabs, state => ({
    ...state,
    tabs: {},
  })),

  on(FeedActions.deletePostFromTab, (state, { postId, tabId }) => {
    if (tabId && state.tabs[tabId as CommunityTabType]) {
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...state.tabs[tabId as CommunityTabType]!,
            items: state.tabs[tabId as CommunityTabType]!.items.filter(item => item.post_uuid !== postId),
          },
        },
      };
    }

    // Если tabId не указан, удаляем из всех табов
    const updatedTabs = Object.keys(state.tabs).reduce(
      (acc, currentTabId) => {
        acc[currentTabId] = {
          ...state.tabs[currentTabId as CommunityTabType]!,
          items: state.tabs[currentTabId as CommunityTabType]!.items.filter(item => item.post_uuid !== postId),
        };
        return acc;
      },
      {} as { [tabId: string]: FeedTabData }
    );

    return {
      ...state,
      tabs: updatedTabs,
    };
  }),

  on(FeedActions.setLastViewedPost, (state, { tabId, postId }) => ({
    ...state,
    tabs: {
      ...state.tabs,
      [tabId]: {
        ...(state.tabs[tabId as CommunityTabType] || {
          items: [],
          currentPage: 0,
          hasMore: false,
          isLoading: false,
          scrollPosition: 0,
        }),
        lastViewedPostId: postId,
      },
    },
  })),

  on(FeedActions.setTargetPost, (state, { tabId, postId }) => ({
    ...state,
    tabs: {
      ...state.tabs,
      [tabId]: {
        ...(state.tabs[tabId as CommunityTabType] || {
          items: [],
          currentPage: 0,
          hasMore: false,
          isLoading: false,
          scrollPosition: 0,
        }),
        targetPostId: postId,
      },
    },
  })),

  on(FeedActions.clearTargetPost, (state, { tabId }) => {
    const existingTabData = state.tabs[tabId as CommunityTabType];
    if (!existingTabData) {
      return state;
    }

    return {
      ...state,
      tabs: {
        ...state.tabs,
        [tabId]: {
          ...existingTabData,
          targetPostId: undefined,
        },
      },
    };
  }),

  on(FeedActions.setLastActiveTab, (state, { tabId }) => ({
    ...state,
    lastActiveTab: tabId,
  })),

  on(FeedActions.clearLastActiveTab, state => ({
    ...state,
    lastActiveTab: undefined as CommunityTabType | undefined,
  })),

  on(FeedActions.updatePostItem, (state, { postUuid, changes }) => {
    const updatedTabs = Object.keys(state.tabs).reduce(
      (acc, tabId) => {
        const tab = state.tabs[tabId as CommunityTabType]!;
        acc[tabId] = {
          ...tab,
          items: tab.items.map(item =>
            item.post_uuid === postUuid ? { ...item, ...changes } : item
          ),
        };
        return acc;
      },
      {} as { [tabId: string]: FeedTabData }
    );

    return { ...state, tabs: updatedTabs };
  })
);
